import React, { useEffect, useState } from "react";
import {
  MessageCircle,
  Database,
  FileText,
  HelpCircle,
  Zap,
  Shield,
  Clock,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Sparkles,
  PlayCircle,
} from "lucide-react";

export const WelcomeScreen: React.FC<{
  themeMode?: "light" | "dark";
  accessMode?: "user" | "admin";
}> = ({ themeMode = "light", accessMode = "admin" }) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(
    "quickStart"
  );
  const [quickStartQueries, setQuickStartQueries] = useState<string[]>([
    "List all tenants",
    "What's the user count per tenant?",
    "What are the system requirements?",
  ]);
  const [suggestedQueries, setSuggestedQueries] = useState([
    {
      query: "List all tenants",
      type: "mysql",
      description: "List all tenant records from the database",
    },
    {
      query: "What's the user count per tenant?",
      type: "mysql",
      description: "Get aggregated user statistics by tenant",
    },
    {
      query: "Show me tenants with violation scores above 500",
      type: "mysql",
      description: "Filter tenants by violation score threshold",
    },
    {
      query: "How do I implement vector databases in RAG?",
      type: "document",
      description: "Search technical documentation",
    },
    {
      query: "How to reset password?",
      type: "faq",
      description: "Get answers from FAQ knowledge base",
    },
    {
      query: "Tell me about recent tenant registrations",
      type: "mysql",
      description: "Query recent tenant creation data",
    },
  ]);

  const isDark = themeMode === "dark";

  useEffect(() => {
    if (accessMode === "user") {
      const newStartQueries = [
        "Show my tenant information",
        "What's the user count per tenant?",
        "What are the system requirements?",
      ];
      setQuickStartQueries(newStartQueries);

      const newSuggestedQueries = [
        {
          query: "Show my tenant information",
          type: "mysql",
          description: "Return tenant details from the database",
        },
        {
          query: "What's the user count per tenant?",
          type: "mysql",
          description: "Get aggregated user statistics by tenant",
        },
        {
          query: "What is my violation score?",
          type: "mysql",
          description: "Return violation score threshold",
        },
        {
          query: "How do I implement vector databases in RAG?",
          type: "document",
          description: "Search technical documentation",
        },
        {
          query: "How to reset password?",
          type: "faq",
          description: "Get answers from FAQ knowledge base",
        },
        {
          query: "Tell me about recent user registrations",
          type: "mysql",
          description: "Query recent user creation data",
        },
      ];

      setSuggestedQueries(newSuggestedQueries);
    }
  }, [accessMode]);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const getQueryIcon = (type: string) => {
    switch (type) {
      case "mysql":
        return <Database className="w-4 h-4 text-orange-600" />;
      case "document":
        return <FileText className="w-4 h-4 text-orange-600" />;
      case "faq":
        return <HelpCircle className="w-4 h-4 text-orange-600" />;
      default:
        return <MessageCircle className="w-4 h-4 text-orange-600" />;
    }
  };

  const handleQuickQuery = (query: string) => {
    const event = new CustomEvent("sendSuggestedQuery", {
      detail: query,
    });
    window.dispatchEvent(event);
  };

  return (
    <div
      className={`flex flex-col h-full overflow-y-auto ${
        isDark ? "bg-[#14161C] text-gray-100" : "bg-white text-gray-800"
      }`}
    >
      {/* Compact Header */}
      <div className="text-center p-4">
        <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
          <Sparkles className="w-6 h-6 text-white" />
        </div>
        <h3
          className={`text-lg font-bold mb-1 ${isDark ? "text-gray-100" : "text-gray-800"}`}
        >
          Welcome! I'm your NopalCyber Assistant
        </h3>
        <p
          className={`text-xs leading-relaxed px-2 ${
            isDark ? "text-gray-400" : "text-gray-600"
          }`}
        >
          Ask me anything about your data, documents, or systems using natural
          language
        </p>
      </div>

      {/* Quick Start Actions */}
      <div className="px-4 pb-3">
        <div
          className={`rounded-xl p-3 border ${
            isDark
              ? "bg-[#1C1F24] border-[#1C1F24]"
              : "bg-orange-50 border-orange-200"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <PlayCircle className="w-4 h-4 text-orange-500" />
            <h4 className="text-sm font-semibold text-orange-700">
              Quick Start
            </h4>
          </div>
          <div className="grid grid-cols-1 gap-1.5">
            {quickStartQueries.map((query, index) => (
              <button
                key={index}
                className={`text-left text-xs rounded-lg px-3 py-2 transition-all duration-200 hover:shadow-sm border ${
                  isDark
                    ? "bg-[#1C1F24] border-[#1C1F24] hover:bg-gray-300/5"
                    : "bg-white border-orange-200 hover:bg-orange-100 hover:border-orange-300"
                }`}
                onClick={() => handleQuickQuery(query)}
              >
                <span className="text-orange-600 font-medium">"{query}"</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Collapsible Data Sources */}
      <div className="px-4 pb-1">
        <button
          onClick={() => toggleSection("sources")}
          className={`w-full flex items-center justify-between p-2 text-left rounded-lg transition-colors hover:shadow-sm hover:scale-[1.005] ${
            isDark
              ? "hover:bg-gray-300/10"
              : "bg-white hover:bg-gradient-to-r hover:from-orange-50 hover:to-white border-gray-200 hover:border-orange-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-orange-600" />
            <h4
              className={`text-sm font-medium ${
                isDark
                  ? "text-gray-100 group-hover:text-white"
                  : "text-gray-800 group-hover:text-gray-900"
              }`}
            >
              Data Sources
            </h4>
            <span
              className={`text-xs ${isDark ? "text-white bg-[#1C1F24]" : "text-gray-500 bg-gray-100"} px-2 py-0.5 rounded-full`}
            >
              3
            </span>
          </div>
          {expandedSection === "sources" ? (
            <ChevronDown className="w-4 h-4 text-orange-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-orange-400" />
          )}
        </button>

        {expandedSection === "sources" && (
          <div className="mt-2 space-y-2 pl-6">
            <div className="flex items-center gap-2 text-xs">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-gray-300 font-medium">MySQL Databases</span>
              <span className="text-white">- Real-time data queries</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-gray-300 font-medium">Documents</span>
              <span className="text-white">- PDF, DOCX, XLSX files</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-gray-300 font-medium">FAQ Base</span>
              <span className="text-white">- Instant Q&A answers</span>
            </div>
          </div>
        )}
      </div>

      {/* Collapsible More Examples */}
      <div className="px-4 pb-1">
        <button
          onClick={() => toggleSection("examples")}
          className={`w-full flex items-center justify-between p-2 text-left rounded-lg transition-colors hover:shadow-sm hover:scale-[1.005] ${
            isDark
              ? "hover:bg-gray-300/10"
              : "bg-white hover:bg-gradient-to-r hover:from-orange-50 hover:to-white border-gray-200 hover:border-orange-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <h4
              className={`text-sm font-medium ${
                isDark
                  ? "text-gray-100 group-hover:text-white"
                  : "text-gray-800 group-hover:text-gray-900"
              }`}
            >
              More Examples
            </h4>
            <span
              className={`text-xs ${isDark ? "text-white bg-[#1C1F24]" : "text-gray-500 bg-gray-100"} px-2 py-0.5 rounded-full`}
            >
              6
            </span>
          </div>
          {expandedSection === "examples" ? (
            <ChevronDown className="w-4 h-4 text-orange-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-orange-400" />
          )}
        </button>

        {expandedSection === "examples" && (
          <div className="mt-2 space-y-1.5 pl-6 max-h-40 overflow-y-auto">
            {suggestedQueries.slice(0, 4).map((item, index) => (
              <button
                key={index}
                className={`w-full text-left p-2 text-xs rounded-lg hover:bg-gray-300/10 hover:shadow-sm ${isDark ? "bg-[#1C1F24] border-[#1C1F24]" : ""} transition-colors`}
                onClick={() => handleQuickQuery(item.query)}
              >
                <div className="flex items-center gap-2">
                  {getQueryIcon(item.type)}
                  <span
                    className={`font-medium truncate ${
                      isDark
                        ? "text-gray-100 hover:text-white"
                        : "text-gray-800 hover:text-gray-900"
                    }`}
                  >
                    "{item.query}"
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Collapsible Features */}
      <div className="px-4 pb-2">
        <button
          onClick={() => toggleSection("features")}
          className={`w-full flex items-center justify-between p-2 text-left rounded-lg transition-colors hover:shadow-sm hover:scale-[1.005] ${
            isDark
              ? "hover:bg-gray-300/10"
              : "bg-white hover:bg-gradient-to-r hover:from-orange-50 hover:to-white border-gray-200 hover:border-orange-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-orange-600" />
            <h4
              className={`text-sm font-medium ${
                isDark
                  ? "text-gray-100 group-hover:text-white"
                  : "text-gray-800 group-hover:text-gray-900"
              }`}
            >
              Key Features
            </h4>
          </div>
          {expandedSection === "features" ? (
            <ChevronDown className="w-4 h-4 text-orange-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-orange-400" />
          )}
        </button>

        {expandedSection === "features" && (
          <div className="mt-2 space-y-1.5 pl-6">
            <div className="flex items-center gap-2 text-xs">
              <TrendingUp className="w-3 h-3 text-orange-500" />
              <span>Natural language to SQL translation</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Shield className="w-3 h-3 text-orange-500" />
              <span>Secure read-only database access</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Clock className="w-3 h-3 text-orange-500" />
              <span>Real-time results with performance metrics</span>
            </div>
          </div>
        )}
      </div>

      {/* Compact Tips */}
      {accessMode !== "user" && (
        <div className="px-4 pb-4 mt-auto">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-3 border border-amber-200">
            <div className="flex items-start gap-2">
              <div className="text-lg">💡</div>
              <div>
                <h4 className="text-xs font-semibold text-amber-800 mb-1">
                  Pro Tip
                </h4>
                <p className="text-xs text-amber-700 leading-relaxed">
                  Be specific in your queries: "Show me tenants created this
                  week with violation scores above 500"
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
